import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Garden, Schedule } from '@/types';
import { mockIrrigationEvents, mockSchedules, mockControllers } from '@/data/mockData';
import {
  History,
  Calendar,
  Thermometer,
  Droplets,
  CloudSun,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Play,
  Square,
  Cpu,
  Timer,
  Zap,
  CloudRain,
  ChevronLeft,
  ChevronRight,
  Activity,
  BarChart3,
  Leaf,
  Wind,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  ReferenceLine,
  Legend,
  Cell,
} from 'recharts';
import { format, subDays, isSameDay, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface GardenHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garden: Garden | null;
}

interface HourlyValveData {
  hour: string;
  hourNum: number;
  expected: 0 | 1;
  actual: 0 | 1 | -1;
  hasMismatch: boolean;
  soilTemp?: number;
  soilHumidity?: number;
}

function generateHourlyData(
  garden: Garden,
  date: Date,
  controllerId: string,
  valveChannel: number
): HourlyValveData[] {
  const data: HourlyValveData[] = [];
  const dayOfWeek = date.getDay();
  
  const schedules: { schedule: Schedule; startMin: number; endMin: number }[] = [];
  garden.zones.forEach(zone => {
    zone.scheduleAssignments?.forEach(sa => {
      if (sa.controllerId === controllerId && sa.valveChannel === valveChannel) {
        const schedule = mockSchedules.find(s => s.id === sa.scheduleId);
        if (schedule?.enabled && schedule.daysOfWeek.includes(dayOfWeek)) {
          const [h, m] = schedule.startTime.split(':').map(Number);
          const startMin = h * 60 + m;
          const endMin = startMin + schedule.duration;
          schedules.push({ schedule, startMin, endMin });
        }
      }
    });
  });
  
  const dayEvents = mockIrrigationEvents.filter(
    e => e.gardenId === garden.id && 
         isSameDay(e.timestamp, date) && 
         e.controllerId === controllerId &&
         e.valveChannel === valveChannel
  );
  
  for (let hour = 0; hour < 24; hour++) {
    const hourStart = hour * 60;
    const hourEnd = (hour + 1) * 60;
    const hasExpected = schedules.some(s => (s.startMin < hourEnd && s.endMin > hourStart));
    const hourEvents = dayEvents.filter(e => e.timestamp.getHours() === hour);
    
    let actualState: 0 | 1 | -1 = -1;
    if (hourEvents.length > 0) {
      const hasStart = hourEvents.some(e => e.eventType === 'start');
      actualState = hasStart ? 1 : 0;
    } else if (hasExpected) {
      actualState = Math.random() > 0.1 ? 1 : 0;
    }
    
    const baseTemp = 18 + Math.sin((hour - 6) * Math.PI / 12) * 8;
    const baseHum = 45 - Math.sin((hour - 6) * Math.PI / 12) * 15;
    
    data.push({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      hourNum: hour,
      expected: hasExpected ? 1 : 0,
      actual: actualState,
      hasMismatch: hasExpected && actualState !== -1 && (hasExpected ? 1 : 0) !== actualState,
      soilTemp: Math.round((baseTemp + Math.random() * 2) * 10) / 10,
      soilHumidity: Math.round(Math.max(20, Math.min(80, baseHum + Math.random() * 10))),
    });
  }
  
  return data;
}

function generateStats(garden: Garden) {
  const now = new Date();
  let totalScheduled = 0, totalExecuted = 0, startErrors = 0, stopErrors = 0;
  let triggerStarts = 0, triggerStops = 0, manualActions = 0;
  
  for (let day = 6; day >= 0; day--) {
    const date = subDays(now, day);
    const dayOfWeek = date.getDay();
    
    garden.zones.forEach(zone => {
      zone.scheduleAssignments?.forEach(sa => {
        const schedule = mockSchedules.find(s => s.id === sa.scheduleId);
        if (schedule?.enabled && schedule.daysOfWeek.includes(dayOfWeek)) totalScheduled++;
      });
    });
    
    const dayEvents = mockIrrigationEvents.filter(
      e => e.gardenId === garden.id && isSameDay(e.timestamp, date)
    );
    
    dayEvents.forEach(event => {
      if (event.source === 'schedule') {
        if (event.eventType === 'start') totalExecuted++;
        if (event.expectedState !== event.actualState) {
          if (event.eventType === 'start') startErrors++;
          else stopErrors++;
        }
      } else if (event.source === 'trigger') {
        if (event.eventType === 'start') triggerStarts++;
        else triggerStops++;
      } else if (event.source === 'manual') {
        manualActions++;
      }
    });
  }
  
  return {
    totalScheduled, totalExecuted, startErrors, stopErrors,
    triggerStarts, triggerStops, manualActions,
    successRate: totalScheduled > 0 ? Math.round(((totalExecuted - startErrors) / totalScheduled) * 100) : 100,
  };
}

function generateSoilSensorData() {
  const data = [];
  const now = new Date();
  
  for (let day = 6; day >= 0; day--) {
    const date = subDays(now, day);
    const dayVariation = Math.random() * 4 - 2;
    
    data.push({
      date: format(date, 'EEE dd', { locale: es }),
      soilTempMax: Math.round((22 + 6 + dayVariation) * 10) / 10,
      soilTempMin: Math.round((22 - 4 + dayVariation) * 10) / 10,
      soilTempAvg: Math.round((22 + dayVariation) * 10) / 10,
      soilHumMax: Math.round(Math.min(80, 50 + 15 + Math.random() * 10)),
      soilHumMin: Math.round(Math.max(20, 50 - 20 + Math.random() * 5)),
      soilHumAvg: Math.round(50 + Math.random() * 5),
      conductivity: Math.round(100 + Math.random() * 60),
    });
  }
  return data;
}

function generateAemetData() {
  const data = [];
  const now = new Date();
  
  for (let day = 6; day >= 0; day--) {
    const date = subDays(now, day);
    const temp = 20 + Math.random() * 12;
    const hum = 40 + Math.random() * 40;
    const rainProb = Math.random() * 100;
    const rainMm = rainProb > 60 ? Math.random() * 10 : 0;
    
    data.push({
      date: format(date, 'EEE dd', { locale: es }),
      airTempMax: Math.round((temp + 6) * 10) / 10,
      airTempMin: Math.round((temp - 6) * 10) / 10,
      airTempAvg: Math.round(temp * 10) / 10,
      airHumidity: Math.round(hum),
      rainProbability: Math.round(rainProb),
      rainMm: Math.round(rainMm * 10) / 10,
      condition: rainProb > 70 ? 'rain' : rainProb > 40 ? 'cloudy' : 'sunny',
      windSpeed: Math.round(5 + Math.random() * 20),
    });
  }
  return data;
}

export function GardenHistoryDialog({ open, onOpenChange, garden }: GardenHistoryDialogProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedController, setSelectedController] = useState<string>('');
  const [selectedValve, setSelectedValve] = useState<number>(1);
  
  useMemo(() => {
    if (garden && garden.controllers.length > 0 && !selectedController) {
      setSelectedController(garden.controllers[0].id);
    }
  }, [garden, selectedController]);
  
  const stats = useMemo(() => garden ? generateStats(garden) : null, [garden]);
  const soilData = useMemo(() => generateSoilSensorData(), []);
  const aemetData = useMemo(() => generateAemetData(), []);
  const hourlyData = useMemo(() => {
    if (!garden || !selectedController) return [];
    return generateHourlyData(garden, selectedDate, selectedController, selectedValve);
  }, [garden, selectedDate, selectedController, selectedValve]);
  
  const controller = useMemo(() => mockControllers.find(c => c.id === selectedController), [selectedController]);
  
  const combinedData = useMemo(() => {
    return soilData.map((soil, i) => ({
      ...soil,
      airTempAvg: aemetData[i]?.airTempAvg,
      airHumidity: aemetData[i]?.airHumidity,
    }));
  }, [soilData, aemetData]);
  
  if (!garden) return null;
  
  const handlePrevDay = () => setSelectedDate(prev => subDays(prev, 1));
  const handleNextDay = () => {
    const tomorrow = addDays(selectedDate, 1);
    if (tomorrow <= new Date()) setSelectedDate(tomorrow);
  };
  
  const todaySelected = isSameDay(selectedDate, new Date());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[950px] h-[85vh] max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Histórico - {garden.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Últimos 7 días • {garden.municipality}
          </p>
        </DialogHeader>
        
        <Tabs defaultValue="timeline" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="px-6 py-3 flex-shrink-0 bg-muted/30">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="timeline" className="gap-1.5 text-xs sm:text-sm">
                <Timer className="w-4 h-4" />
                <span className="hidden sm:inline">Timeline</span>
              </TabsTrigger>
              <TabsTrigger value="stats" className="gap-1.5 text-xs sm:text-sm">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Estadísticas</span>
              </TabsTrigger>
              <TabsTrigger value="soil" className="gap-1.5 text-xs sm:text-sm">
                <Leaf className="w-4 h-4" />
                Suelo
              </TabsTrigger>
              <TabsTrigger value="aemet" className="gap-1.5 text-xs sm:text-sm">
                <CloudSun className="w-4 h-4" />
                AEMET
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TIMELINE TAB */}
          <TabsContent value="timeline" className="flex-1 m-0 overflow-auto">
            <div className="p-6 space-y-4">
              {/* Selector */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={handlePrevDay}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="text-center min-w-[140px]">
                    <p className="font-medium">{format(selectedDate, "EEEE, d MMM", { locale: es })}</p>
                    {todaySelected && <Badge variant="secondary" className="text-xs">Hoy</Badge>}
                  </div>
                  <Button variant="outline" size="icon" onClick={handleNextDay} disabled={todaySelected}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={selectedController} onValueChange={setSelectedController}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Controlador" />
                    </SelectTrigger>
                    <SelectContent>
                      {garden.controllers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedValve.toString()} onValueChange={v => setSelectedValve(Number(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue placeholder="Válvula" />
                    </SelectTrigger>
                    <SelectContent>
                      {controller?.valves.map(v => (
                        <SelectItem key={v.channel} value={v.channel.toString()}>V{v.channel}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Leyenda */}
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-primary/40" /><span>Programado</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-success" /><span>Real ON</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-muted-foreground" /><span>Real OFF</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-destructive" /><span>Discrepancia</span></div>
              </div>

              {/* Gráfico Programado vs Real */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-primary" />
                    Estado Válvula: Programado vs Real
                    <Badge variant="outline" className="ml-2">{controller?.name} - V{selectedValve}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[140px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={hourlyData} barGap={0} barCategoryGap="15%">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                        <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                        <YAxis domain={[-0.1, 1.1]} ticks={[0, 1]} tickFormatter={(v) => v === 1 ? 'ON' : 'OFF'} tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} formatter={(v: number, n: string) => [v === 1 ? 'ON' : v === 0 ? 'OFF' : 'Sin datos', n]} />
                        <Legend />
                        <Bar dataKey="expected" name="Programado" fill="hsl(var(--primary))" fillOpacity={0.4} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="actual" name="Real" radius={[2, 2, 0, 0]}>
                          {hourlyData.map((entry, i) => (
                            <Cell key={i} fill={entry.actual === -1 ? 'transparent' : entry.hasMismatch ? 'hsl(var(--destructive))' : entry.actual === 1 ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))'} fillOpacity={entry.actual === -1 ? 0 : 0.8} />
                          ))}
                        </Bar>
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  {hourlyData.some(h => h.hasMismatch) && (
                    <div className="mt-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-destructive" />
                      <span className="text-xs text-destructive">{hourlyData.filter(h => h.hasMismatch).length} hora(s) con discrepancia</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Datos Suelo + Estado Válvula */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-success" />
                    Datos Suelo + Estado Válvula
                    <Badge variant="outline" className="bg-success/10 text-success border-success/20">Efecto Riego</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={hourlyData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                        <YAxis yAxisId="temp" tick={{ fontSize: 10 }} domain={[10, 35]} />
                        <YAxis yAxisId="hum" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        <Legend />
                        <Bar yAxisId="hum" dataKey={(d: HourlyValveData) => d.actual === 1 ? 15 : 0} name="Válvula ON" fill="hsl(var(--primary))" fillOpacity={0.5} radius={[2, 2, 0, 0]} />
                        <Line yAxisId="temp" type="monotone" dataKey="soilTemp" stroke="hsl(var(--warning))" strokeWidth={2} dot={false} name="Temp Suelo (°C)" />
                        <Area yAxisId="hum" type="monotone" dataKey="soilHumidity" stroke="hsl(var(--info))" fill="hsl(var(--info) / 0.3)" name="Humedad Suelo (%)" />
                        <ReferenceLine yAxisId="hum" y={25} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="hum" y={70} stroke="hsl(var(--success))" strokeDasharray="3 3" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Eventos del día */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Eventos Recibidos</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const dayEvents = mockIrrigationEvents.filter(e => e.gardenId === garden.id && isSameDay(e.timestamp, selectedDate) && e.controllerId === selectedController && e.valveChannel === selectedValve).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                    if (dayEvents.length === 0) return <p className="text-sm text-muted-foreground text-center py-4">Sin eventos para esta válvula</p>;
                    return (
                      <div className="space-y-2 max-h-[150px] overflow-y-auto">
                        {dayEvents.map(event => {
                          const hasMismatch = event.expectedState !== event.actualState;
                          return (
                            <div key={event.id} className={cn('flex items-center justify-between p-2 rounded-lg', hasMismatch ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted/50')}>
                              <div className="flex items-center gap-2">
                                <div className={cn('p-1.5 rounded-full', event.eventType === 'start' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground')}>
                                  {event.eventType === 'start' ? <Play className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{format(event.timestamp, 'HH:mm:ss')} - {event.eventType === 'start' ? 'Apertura' : 'Cierre'}</p>
                                  <p className="text-xs text-muted-foreground">{event.sourceName}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={cn('text-xs', event.source === 'schedule' && 'bg-primary/10 text-primary', event.source === 'trigger' && 'bg-warning/10 text-warning', event.source === 'manual' && 'bg-muted')}>
                                  {event.source === 'schedule' ? 'Programado' : event.source === 'trigger' ? 'Trigger' : 'Manual'}
                                </Badge>
                                {hasMismatch ? <XCircle className="w-4 h-4 text-destructive" /> : <CheckCircle2 className="w-4 h-4 text-success" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* STATS TAB */}
          <TabsContent value="stats" className="flex-1 m-0 overflow-auto">
            <div className="p-6 space-y-4">
              {stats && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-sm text-muted-foreground">Programados</span><Calendar className="w-4 h-4 text-primary" /></div>
                      <p className="text-3xl font-bold">{stats.totalScheduled}</p>
                      <p className="text-xs text-muted-foreground">ciclos en 7 días</p>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-sm text-muted-foreground">Ejecutados</span><CheckCircle2 className="w-4 h-4 text-success" /></div>
                      <p className="text-3xl font-bold text-success">{stats.totalExecuted}</p>
                      <p className="text-xs text-muted-foreground">arranques correctos</p>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-sm text-muted-foreground">Tasa Éxito</span><Activity className="w-4 h-4 text-info" /></div>
                      <p className={cn('text-3xl font-bold', stats.successRate >= 90 ? 'text-success' : stats.successRate >= 70 ? 'text-warning' : 'text-destructive')}>{stats.successRate}%</p>
                      <p className="text-xs text-muted-foreground">de ejecución</p>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-sm text-muted-foreground">Triggers</span><Zap className="w-4 h-4 text-warning" /></div>
                      <p className="text-3xl font-bold text-warning">{stats.triggerStarts + stats.triggerStops}</p>
                      <p className="text-xs text-muted-foreground">activaciones</p>
                    </Card>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-medium mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" />Errores de Activación/Paro</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <Card className={cn('p-4', stats.startErrors > 0 && 'border-destructive/50 bg-destructive/5')}>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-destructive/10"><Play className="w-5 h-5 text-destructive" /></div>
                          <div>
                            <p className="text-2xl font-bold text-destructive">{stats.startErrors}</p>
                            <p className="text-sm text-muted-foreground">Errores Arranque</p>
                          </div>
                        </div>
                      </Card>
                      <Card className={cn('p-4', stats.stopErrors > 0 && 'border-destructive/50 bg-destructive/5')}>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-destructive/10"><Square className="w-5 h-5 text-destructive" /></div>
                          <div>
                            <p className="text-2xl font-bold text-destructive">{stats.stopErrors}</p>
                            <p className="text-sm text-muted-foreground">Errores Paro</p>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-medium mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-warning" />Activaciones por Trigger</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="p-4 bg-warning/5 border-warning/20">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-warning/10"><Play className="w-5 h-5 text-warning" /></div>
                          <div><p className="text-2xl font-bold text-warning">{stats.triggerStarts}</p><p className="text-sm text-muted-foreground">Arranques</p></div>
                        </div>
                      </Card>
                      <Card className="p-4 bg-warning/5 border-warning/20">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-warning/10"><Square className="w-5 h-5 text-warning" /></div>
                          <div><p className="text-2xl font-bold text-warning">{stats.triggerStops}</p><p className="text-sm text-muted-foreground">Paros</p></div>
                        </div>
                      </Card>
                      <Card className="p-4 bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-muted"><Cpu className="w-5 h-5 text-muted-foreground" /></div>
                          <div><p className="text-2xl font-bold">{stats.manualActions}</p><p className="text-sm text-muted-foreground">Manuales</p></div>
                        </div>
                      </Card>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* SOIL TAB */}
          <TabsContent value="soil" className="flex-1 m-0 overflow-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20">
                <Leaf className="w-5 h-5 text-success" />
                <div>
                  <p className="font-medium text-success">Datos de Sensores del Suelo</p>
                  <p className="text-xs text-muted-foreground">Temperatura y humedad medidas directamente en el terreno</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Thermometer className="w-4 h-4 text-warning" />Temp. Suelo</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{soilData[soilData.length - 1]?.soilTempAvg}°C</div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div><span className="text-muted-foreground">Mín</span><p className="font-medium">{Math.min(...soilData.map(d => d.soilTempMin))}°C</p></div>
                      <div><span className="text-muted-foreground">Media</span><p className="font-medium">{Math.round(soilData.reduce((a, d) => a + d.soilTempAvg, 0) / soilData.length * 10) / 10}°C</p></div>
                      <div><span className="text-muted-foreground">Máx</span><p className="font-medium">{Math.max(...soilData.map(d => d.soilTempMax))}°C</p></div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Droplets className="w-4 h-4 text-info" />Humedad Suelo</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-info">{soilData[soilData.length - 1]?.soilHumAvg}%</div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div><span className="text-muted-foreground">Mín</span><p className="font-medium">{Math.min(...soilData.map(d => d.soilHumMin))}%</p></div>
                      <div><span className="text-muted-foreground">Media</span><p className="font-medium">{Math.round(soilData.reduce((a, d) => a + d.soilHumAvg, 0) / soilData.length)}%</p></div>
                      <div><span className="text-muted-foreground">Máx</span><p className="font-medium">{Math.max(...soilData.map(d => d.soilHumMax))}%</p></div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Zap className="w-4 h-4" />Conductividad</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{soilData[soilData.length - 1]?.conductivity} µS/cm</div>
                    <p className="text-xs text-muted-foreground mt-2">Media: {Math.round(soilData.reduce((a, d) => a + d.conductivity, 0) / soilData.length)} µS/cm</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Temperatura Suelo - 7 días</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={soilData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} domain={['dataMin - 5', 'dataMax + 5']} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <Area type="monotone" dataKey="soilTempMax" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive) / 0.2)" name="Máxima (°C)" />
                        <Area type="monotone" dataKey="soilTempAvg" stroke="hsl(var(--warning))" fill="hsl(var(--warning) / 0.3)" name="Media (°C)" />
                        <Area type="monotone" dataKey="soilTempMin" stroke="hsl(var(--info))" fill="hsl(var(--info) / 0.2)" name="Mínima (°C)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Humedad Suelo - 7 días</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={soilData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <ReferenceLine y={25} stroke="hsl(var(--destructive))" strokeDasharray="3 3" />
                        <ReferenceLine y={70} stroke="hsl(var(--success))" strokeDasharray="3 3" />
                        <Area type="monotone" dataKey="soilHumMax" stroke="hsl(var(--info))" fill="hsl(var(--info) / 0.2)" name="Máxima (%)" />
                        <Area type="monotone" dataKey="soilHumAvg" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.3)" name="Media (%)" />
                        <Area type="monotone" dataKey="soilHumMin" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted) / 0.5)" name="Mínima (%)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Comparativa: Suelo vs Ambiente</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={combinedData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <Legend />
                        <Line type="monotone" dataKey="soilTempAvg" stroke="hsl(var(--success))" strokeWidth={2} dot={{ r: 3 }} name="Temp. Suelo (°C)" />
                        <Line type="monotone" dataKey="airTempAvg" stroke="hsl(var(--warning))" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} name="Temp. Ambiente (°C)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AEMET TAB */}
          <TabsContent value="aemet" className="flex-1 m-0 overflow-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-info/10 border border-info/20">
                <CloudSun className="w-5 h-5 text-info" />
                <div>
                  <p className="font-medium text-info">Datos Meteorológicos AEMET</p>
                  <p className="text-xs text-muted-foreground">Condiciones atmosféricas para {garden.municipality}</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <Card className="p-3">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Temp. Aire</span><Thermometer className="w-3.5 h-3.5 text-warning" /></div>
                  <p className="text-xl font-bold">{aemetData[aemetData.length - 1]?.airTempAvg}°C</p>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Humedad Rel.</span><Droplets className="w-3.5 h-3.5 text-info" /></div>
                  <p className="text-xl font-bold text-info">{aemetData[aemetData.length - 1]?.airHumidity}%</p>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Precipitación</span><CloudRain className="w-3.5 h-3.5 text-primary" /></div>
                  <p className="text-xl font-bold">{Math.round(aemetData.reduce((a, d) => a + d.rainMm, 0) * 10) / 10} mm</p>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Viento</span><Wind className="w-3.5 h-3.5 text-muted-foreground" /></div>
                  <p className="text-xl font-bold">{aemetData[aemetData.length - 1]?.windSpeed} km/h</p>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Temperatura Ambiente - 7 días</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={aemetData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <Area type="monotone" dataKey="airTempMax" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive) / 0.2)" name="Máxima (°C)" />
                        <Area type="monotone" dataKey="airTempMin" stroke="hsl(var(--info))" fill="hsl(var(--info) / 0.2)" name="Mínima (°C)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Precipitación y Prob. Lluvia</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={aemetData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={[0, 100]} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                        <Legend />
                        <Bar yAxisId="right" dataKey="rainMm" fill="hsl(var(--primary))" name="Precipitación (mm)" />
                        <Line yAxisId="left" type="monotone" dataKey="rainProbability" stroke="hsl(var(--info))" strokeWidth={2} dot={{ r: 3 }} name="Prob. Lluvia (%)" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Previsión por Día</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2">
                    {aemetData.map((day, i) => (
                      <div key={i} className={cn('p-2 rounded-lg text-center', i === aemetData.length - 1 ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50')}>
                        <p className="text-xs font-medium capitalize">{day.date}</p>
                        <div className="my-1.5">
                          {day.condition === 'sunny' && <CloudSun className="w-5 h-5 mx-auto text-warning" />}
                          {day.condition === 'cloudy' && <CloudSun className="w-5 h-5 mx-auto text-muted-foreground" />}
                          {day.condition === 'rain' && <CloudRain className="w-5 h-5 mx-auto text-info" />}
                        </div>
                        <p className="text-sm font-bold">{day.airTempMax}°</p>
                        <p className="text-xs text-muted-foreground">{day.airTempMin}°</p>
                        {day.rainMm > 0 && <p className="text-xs text-info mt-0.5">{day.rainMm}mm</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
